// Auto-generated model registry
// Maps Neo4j node labels to TypeScript class constructors
// DO NOT EDIT - Changes will be overwritten

import { setFrameworkResolvers } from '@sourcepilot/framework/resolvers';
import { getRelationshipPropertyName, getRelationshipClass } from './relationship-registry';
import { isRelationshipAllowed, getNestedSpec, getModifyConstructionSpec } from '../construction-specs';

/**
 * Registry of all model classes by their Neo4j label.
 * Uses lazy initialization to avoid circular dependency issues.
 */
let _registry: Record<string, any> | null = null;

function getRegistry(): Record<string, any> {
  if (_registry) return _registry;

  // Lazy load imports
  const Action = require('../rflp/api/action').Action;
  const ActionDefinition = require('../sysml/api/actiondefinition').ActionDefinition;
  const ActionInputParameter = require('../rflp/api/actioninputparameter').ActionInputParameter;
  const ActionOutputParameter = require('../rflp/api/actionoutputparameter').ActionOutputParameter;
  const ActionParameter = require('../rflp/api/actionparameter').ActionParameter;
  const ActionRoot = require('../pdm/api/actionroot').ActionRoot;
  const ActionUsage = require('../sysml/api/actionusage').ActionUsage;
  const ActionValidationTask = require('../managers/api/actionvalidationtask').ActionValidationTask;
  const AnalysisRecord = require('../rflp/api/analysisrecord').AnalysisRecord;
  const Answer = require('../tasks/api/answer').Answer;
  const AttachedRecord = require('../shared/api/attachedrecord').AttachedRecord;
  const Attribute = require('../rflp/api/attribute').Attribute;
  const AttributeDefinition = require('../sysml/api/attributedefinition').AttributeDefinition;
  const AttributeExpansionTask = require('../managers/api/attributeexpansiontask').AttributeExpansionTask;
  const AttributeUsage = require('../sysml/api/attributeusage').AttributeUsage;
  const AttributeValue = require('../rflp/api/attributevalue').AttributeValue;
  const CalculationState = require('../rflp/api/calculationstate').CalculationState;
  const CatalogNode = require('../shared/api/catalognode').CatalogNode;
  const ChangeImpact = require('../rflp/api/changeimpact').ChangeImpact;
  const ChangeProposal = require('../rflp/api/changeproposal').ChangeProposal;
  const ChatSkillsTask = require('../pdm_tasks/api/chatskillstask').ChatSkillsTask;
  const ChatTask = require('../pdm_tasks/api/chattask').ChatTask;
  const ComponentExtractionTask = require('../managers/api/componentextractiontask').ComponentExtractionTask;
  const ComponentMatchTask = require('../managers/api/componentmatchtask').ComponentMatchTask;
  const Concern = require('../rflp/api/concern').Concern;
  const Connection = require('../rflp/api/connection').Connection;
  const Constraint = require('../rflp/api/constraint').Constraint;
  const ConstraintDefinition = require('../sysml/api/constraintdefinition').ConstraintDefinition;
  const ConstraintUsage = require('../sysml/api/constraintusage').ConstraintUsage;
  const ControlFlow = require('../sysml/api/controlflow').ControlFlow;
  const ControlPort = require('../rflp/api/controlport').ControlPort;
  const DanglingPortResolutionTask = require('../managers/api/danglingportresolutiontask').DanglingPortResolutionTask;
  const DataFlow = require('../sysml/api/dataflow').DataFlow;
  const DatabaseReference = require('../shared/api/databasereference').DatabaseReference;
  const DeduplicationTask = require('../managers/api/deduplicationtask').DeduplicationTask;
  const DerivedExpression = require('../rflp/api/derivedexpression').DerivedExpression;
  const DerivedValue = require('../rflp/api/derivedvalue').DerivedValue;
  const DiscreteAttribute = require('../rflp/api/discreteattribute').DiscreteAttribute;
  const EntityExtractionTask = require('../managers/api/entityextractiontask').EntityExtractionTask;
  const EntityMatchTask = require('../managers/api/entitymatchtask').EntityMatchTask;
  const Expression = require('../rflp/api/expression').Expression;
  const ExtractionSummaryTask = require('../managers/api/extractionsummarytask').ExtractionSummaryTask;
  const FluidFlow = require('../sysml/api/fluidflow').FluidFlow;
  const FunctionMatchTask = require('../managers/api/functionmatchtask').FunctionMatchTask;
  const FunctionalBlockExtractionTask = require('../managers/api/functionalblockextractiontask').FunctionalBlockExtractionTask;
  const FunctionalBreakdownTask = require('../pdm_tasks/api/functionalbreakdowntask').FunctionalBreakdownTask;
  const FunctionalModule = require('../pdm/api/functionalmodule').FunctionalModule;
  const FunctionalSubsystem = require('../pdm/api/functionalsubsystem').FunctionalSubsystem;
  const GlossaryTerm = require('../rflp/api/glossaryterm').GlossaryTerm;
  const Group = require('../base/api/group').Group;
  const Industry = require('../base/api/industry').Industry;
  const InquiriesSummaryTask = require('../managers/api/inquiriessummarytask').InquiriesSummaryTask;
  const InquirySolvingTask = require('../managers/api/inquirysolvingtask').InquirySolvingTask;
  const InterfaceDefinition = require('../sysml/api/interfacedefinition').InterfaceDefinition;
  const InterfaceExpansionTask = require('../managers/api/interfaceexpansiontask').InterfaceExpansionTask;
  const InterfacePort = require('../rflp/api/interfaceport').InterfacePort;
  const IntrinsicExpansionTask = require('../managers/api/intrinsicexpansiontask').IntrinsicExpansionTask;
  const Invite = require('../base/api/invite').Invite;
  const IslandPin = require('../rflp/api/islandpin').IslandPin;
  const IslandRoot = require('../shared/api/islandroot').IslandRoot;
  const Item = require('../rflp/api/item').Item;
  const ItemDefinition = require('../sysml/api/itemdefinition').ItemDefinition;
  const ItemFlow = require('../rflp/api/itemflow').ItemFlow;
  const ItemUsage = require('../sysml/api/itemusage').ItemUsage;
  const LLMTask = require('../llm_task/api/llmtask').LLMTask;
  const LogicalPart = require('../rflp/api/logicalpart').LogicalPart;
  const MBSE_Action = require('../mbse/api/mbse_action').MBSE_Action;
  const MBSE_Attribute = require('../mbse/api/mbse_attribute').MBSE_Attribute;
  const MBSE_Package = require('../mbse/api/mbse_package').MBSE_Package;
  const MBSE_Part = require('../mbse/api/mbse_part').MBSE_Part;
  const MBSE_Port = require('../mbse/api/mbse_port').MBSE_Port;
  const MBSE_Requirement = require('../mbse/api/mbse_requirement').MBSE_Requirement;
  const MainPortExtractionTask = require('../managers/api/mainportextractiontask').MainPortExtractionTask;
  const Material = require('../base/api/material').Material;
  const MaterialExtractionTask = require('../managers/api/materialextractiontask').MaterialExtractionTask;
  const ModelRequest = require('../chat/api/modelrequest').ModelRequest;
  const ModelResponse = require('../chat/api/modelresponse').ModelResponse;
  const ModularBreakdownTask = require('../pdm_tasks/api/modularbreakdowntask').ModularBreakdownTask;
  const ModularRequirementAnalysisTask = require('../pdm_tasks/api/modularrequirementanalysistask').ModularRequirementAnalysisTask;
  const NeoNode = require('../shared/api/neonode').NeoNode;
  const NodeAPI = require('../shared/api/nodeapi').NodeAPI;
  const NodeMergeTask = require('../managers/api/nodemergetask').NodeMergeTask;
  const NumericAttribute = require('../rflp/api/numericattribute').NumericAttribute;
  const Organisation = require('../base/api/organisation').Organisation;
  const PDMAction = require('../pdm/api/pdmaction').PDMAction;
  const PDMItemFlow = require('../pdm/api/pdmitemflow').PDMItemFlow;
  const PDMPart = require('../pdm/api/pdmpart').PDMPart;
  const PDMPort = require('../pdm/api/pdmport').PDMPort;
  const PDMProduct = require('../pdm/api/pdmproduct').PDMProduct;
  const PDMRequirement = require('../pdm/api/pdmrequirement').PDMRequirement;
  const PDMSupplyChainCriteria = require('../pdm/api/pdmsupplychaincriteria').PDMSupplyChainCriteria;
  const Part = require('../rflp/api/part').Part;
  const PartAllocationTask = require('../pdm_tasks/api/partallocationtask').PartAllocationTask;
  const PartBreakdownTask = require('../pdm_tasks/api/partbreakdowntask').PartBreakdownTask;
  const PartDefinition = require('../sysml/api/partdefinition').PartDefinition;
  const PartPerformsTask = require('../pdm_tasks/api/partperformstask').PartPerformsTask;
  const PartUsage = require('../sysml/api/partusage').PartUsage;
  const PhysicalPart = require('../rflp/api/physicalpart').PhysicalPart;
  const PhysicsPort = require('../rflp/api/physicsport').PhysicsPort;
  const Port = require('../rflp/api/port').Port;
  const PortConnectionTask = require('../managers/api/portconnectiontask').PortConnectionTask;
  const PortDefinition = require('../sysml/api/portdefinition').PortDefinition;
  const PortExpansionTask = require('../managers/api/portexpansiontask').PortExpansionTask;
  const PortUsage = require('../sysml/api/portusage').PortUsage;
  const PowerFlow = require('../sysml/api/powerflow').PowerFlow;
  const PrivateNode = require('../shared/api/privatenode').PrivateNode;
  const ProductExtractionTask = require('../managers/api/productextractiontask').ProductExtractionTask;
  const ProductMatchTask = require('../managers/api/productmatchtask').ProductMatchTask;
  const ProductQueryExtractionTask = require('../pdm_tasks/api/productqueryextractiontask').ProductQueryExtractionTask;
  const ProductRequirementsAnalysisTask = require('../pdm_tasks/api/productrequirementsanalysistask').ProductRequirementsAnalysisTask;
  const ProductSupplyChainAnalysisTask = require('../pdm_tasks/api/productsupplychainanalysistask').ProductSupplyChainAnalysisTask;
  const Project = require('../rflp/api/project').Project;
  const ProposalBatch = require('../rflp/api/proposalbatch').ProposalBatch;
  const Question = require('../tasks/api/question').Question;
  const Questionnaire = require('../tasks/api/questionnaire').Questionnaire;
  const RFLPElement = require('../rflp/api/rflpelement').RFLPElement;
  const RFLPTask = require('../rflp_tasks/api/rflptask').RFLPTask;
  const Requirement = require('../rflp/api/requirement').Requirement;
  const RequirementDefinition = require('../sysml/api/requirementdefinition').RequirementDefinition;
  const RequirementExtractionTask = require('../managers/api/requirementextractiontask').RequirementExtractionTask;
  const RequirementMatchTask = require('../managers/api/requirementmatchtask').RequirementMatchTask;
  const RequirementSatisfactionTask = require('../managers/api/requirementsatisfactiontask').RequirementSatisfactionTask;
  const RequirementUsage = require('../sysml/api/requirementusage').RequirementUsage;
  const ResponsePart = require('../chat/api/responsepart').ResponsePart;
  const ScopedNode = require('../shared/api/scopednode').ScopedNode;
  const SecurityScope = require('../shared/api/securityscope').SecurityScope;
  const SemanticMapSummaryTask = require('../managers/api/semanticmapsummarytask').SemanticMapSummaryTask;
  const SimpleFunctionalDependency = require('../pdm/api/simplefunctionaldependency').SimpleFunctionalDependency;
  const SourceDocument = require('../rflp/api/sourcedocument').SourceDocument;
  const StakeholderNeed = require('../rflp/api/stakeholderneed').StakeholderNeed;
  const Standard = require('../rflp/api/standard').Standard;
  const StatementSplitTask = require('../managers/api/statementsplittask').StatementSplitTask;
  const StatementsSummaryTask = require('../managers/api/statementssummarytask').StatementsSummaryTask;
  const Step = require('../chat/api/step').Step;
  const StepTask = require('../llm_task/api/steptask').StepTask;
  const Succession = require('../rflp/api/succession').Succession;
  const SysMLBehavior = require('../sysml/api/sysmlbehavior').SysMLBehavior;
  const SysMLClassifier = require('../sysml/api/sysmlclassifier').SysMLClassifier;
  const SysMLConstraint = require('../sysml/api/sysmlconstraint').SysMLConstraint;
  const SysMLElement = require('../sysml/api/sysmlelement').SysMLElement;
  const SysMLExpression = require('../sysml/api/sysmlexpression').SysMLExpression;
  const SysMLFeature = require('../sysml/api/sysmlfeature').SysMLFeature;
  const SysMLNamespace = require('../sysml/api/sysmlnamespace').SysMLNamespace;
  const SysMLStructure = require('../sysml/api/sysmlstructure').SysMLStructure;
  const SysMLType = require('../sysml/api/sysmltype').SysMLType;
  const SysMLUnit = require('../sysml/api/sysmlunit').SysMLUnit;
  const SysMLUsage = require('../sysml/api/sysmlusage').SysMLUsage;
  const SystemPrompt = require('../chat/api/systemprompt').SystemPrompt;
  const Task = require('../tasks/api/task').Task;
  const TaskHistory = require('../llm_task/api/taskhistory').TaskHistory;
  const TaskNode = require('../tasks/api/tasknode').TaskNode;
  const TaskResult = require('../tasks/api/taskresult').TaskResult;
  const TaskRun = require('../tasks/api/taskrun').TaskRun;
  const TextPart = require('../chat/api/textpart').TextPart;
  const ThinkingPart = require('../chat/api/thinkingpart').ThinkingPart;
  const Thread = require('../chat/api/thread').Thread;
  const ThreadTask = require('../llm_task/api/threadtask').ThreadTask;
  const ToolCallPart = require('../chat/api/toolcallpart').ToolCallPart;
  const ToolCallResponse = require('../chat/api/toolcallresponse').ToolCallResponse;
  const Unit = require('../rflp/api/unit').Unit;
  const UpdatesSummaryTask = require('../managers/api/updatessummarytask').UpdatesSummaryTask;
  const User = require('../base/api/user').User;
  const ValidationTask = require('../managers/api/validationtask').ValidationTask;
  const VersioningBranch = require('../shared/api/versioningbranch').VersioningBranch;
  const VersioningCommit = require('../shared/api/versioningcommit').VersioningCommit;
  const VersioningOp = require('../shared/api/versioningop').VersioningOp;
  const VersioningOrigin = require('../shared/api/versioningorigin').VersioningOrigin;
  const VersioningOverlay = require('../shared/api/versioningoverlay').VersioningOverlay;
  const VersioningPin = require('../shared/api/versioningpin').VersioningPin;
  const VersioningRelease = require('../shared/api/versioningrelease').VersioningRelease;
  const VersioningRow = require('../shared/api/versioningrow').VersioningRow;
  const Workspace = require('../base/api/workspace').Workspace;

  _registry = {
    'Action': Action,
    'ActionDefinition': ActionDefinition,
    'ActionInputParameter': ActionInputParameter,
    'ActionOutputParameter': ActionOutputParameter,
    'ActionParameter': ActionParameter,
    'ActionRoot': ActionRoot,
    'ActionUsage': ActionUsage,
    'ActionValidationTask': ActionValidationTask,
    'AnalysisRecord': AnalysisRecord,
    'Answer': Answer,
    'AttachedRecord': AttachedRecord,
    'Attribute': Attribute,
    'AttributeDefinition': AttributeDefinition,
    'AttributeExpansionTask': AttributeExpansionTask,
    'AttributeUsage': AttributeUsage,
    'AttributeValue': AttributeValue,
    'CalculationState': CalculationState,
    'CatalogNode': CatalogNode,
    'ChangeImpact': ChangeImpact,
    'ChangeProposal': ChangeProposal,
    'ChatSkillsTask': ChatSkillsTask,
    'ChatTask': ChatTask,
    'ComponentExtractionTask': ComponentExtractionTask,
    'ComponentMatchTask': ComponentMatchTask,
    'Concern': Concern,
    'Connection': Connection,
    'Constraint': Constraint,
    'ConstraintDefinition': ConstraintDefinition,
    'ConstraintUsage': ConstraintUsage,
    'ControlFlow': ControlFlow,
    'ControlPort': ControlPort,
    'DanglingPortResolutionTask': DanglingPortResolutionTask,
    'DataFlow': DataFlow,
    'DatabaseReference': DatabaseReference,
    'DeduplicationTask': DeduplicationTask,
    'DerivedExpression': DerivedExpression,
    'DerivedValue': DerivedValue,
    'DiscreteAttribute': DiscreteAttribute,
    'EntityExtractionTask': EntityExtractionTask,
    'EntityMatchTask': EntityMatchTask,
    'Expression': Expression,
    'ExtractionSummaryTask': ExtractionSummaryTask,
    'FluidFlow': FluidFlow,
    'FunctionMatchTask': FunctionMatchTask,
    'FunctionalBlockExtractionTask': FunctionalBlockExtractionTask,
    'FunctionalBreakdownTask': FunctionalBreakdownTask,
    'FunctionalModule': FunctionalModule,
    'FunctionalSubsystem': FunctionalSubsystem,
    'GlossaryTerm': GlossaryTerm,
    'Group': Group,
    'Industry': Industry,
    'InquiriesSummaryTask': InquiriesSummaryTask,
    'InquirySolvingTask': InquirySolvingTask,
    'InterfaceDefinition': InterfaceDefinition,
    'InterfaceExpansionTask': InterfaceExpansionTask,
    'InterfacePort': InterfacePort,
    'IntrinsicExpansionTask': IntrinsicExpansionTask,
    'Invite': Invite,
    'IslandPin': IslandPin,
    'IslandRoot': IslandRoot,
    'Item': Item,
    'ItemDefinition': ItemDefinition,
    'ItemFlow': ItemFlow,
    'ItemUsage': ItemUsage,
    'LLMTask': LLMTask,
    'LogicalPart': LogicalPart,
    'MBSE_Action': MBSE_Action,
    'MBSE_Attribute': MBSE_Attribute,
    'MBSE_Package': MBSE_Package,
    'MBSE_Part': MBSE_Part,
    'MBSE_Port': MBSE_Port,
    'MBSE_Requirement': MBSE_Requirement,
    'MainPortExtractionTask': MainPortExtractionTask,
    'Material': Material,
    'MaterialExtractionTask': MaterialExtractionTask,
    'ModelRequest': ModelRequest,
    'ModelResponse': ModelResponse,
    'ModularBreakdownTask': ModularBreakdownTask,
    'ModularRequirementAnalysisTask': ModularRequirementAnalysisTask,
    'NeoNode': NeoNode,
    'NodeAPI': NodeAPI,
    'NodeMergeTask': NodeMergeTask,
    'NumericAttribute': NumericAttribute,
    'Organisation': Organisation,
    'PDMAction': PDMAction,
    'PDMItemFlow': PDMItemFlow,
    'PDMPart': PDMPart,
    'PDMPort': PDMPort,
    'PDMProduct': PDMProduct,
    'PDMRequirement': PDMRequirement,
    'PDMSupplyChainCriteria': PDMSupplyChainCriteria,
    'Part': Part,
    'PartAllocationTask': PartAllocationTask,
    'PartBreakdownTask': PartBreakdownTask,
    'PartDefinition': PartDefinition,
    'PartPerformsTask': PartPerformsTask,
    'PartUsage': PartUsage,
    'PhysicalPart': PhysicalPart,
    'PhysicsPort': PhysicsPort,
    'Port': Port,
    'PortConnectionTask': PortConnectionTask,
    'PortDefinition': PortDefinition,
    'PortExpansionTask': PortExpansionTask,
    'PortUsage': PortUsage,
    'PowerFlow': PowerFlow,
    'PrivateNode': PrivateNode,
    'ProductExtractionTask': ProductExtractionTask,
    'ProductMatchTask': ProductMatchTask,
    'ProductQueryExtractionTask': ProductQueryExtractionTask,
    'ProductRequirementsAnalysisTask': ProductRequirementsAnalysisTask,
    'ProductSupplyChainAnalysisTask': ProductSupplyChainAnalysisTask,
    'Project': Project,
    'ProposalBatch': ProposalBatch,
    'Question': Question,
    'Questionnaire': Questionnaire,
    'RFLPElement': RFLPElement,
    'RFLPTask': RFLPTask,
    'Requirement': Requirement,
    'RequirementDefinition': RequirementDefinition,
    'RequirementExtractionTask': RequirementExtractionTask,
    'RequirementMatchTask': RequirementMatchTask,
    'RequirementSatisfactionTask': RequirementSatisfactionTask,
    'RequirementUsage': RequirementUsage,
    'ResponsePart': ResponsePart,
    'ScopedNode': ScopedNode,
    'SecurityScope': SecurityScope,
    'SemanticMapSummaryTask': SemanticMapSummaryTask,
    'SimpleFunctionalDependency': SimpleFunctionalDependency,
    'SourceDocument': SourceDocument,
    'StakeholderNeed': StakeholderNeed,
    'Standard': Standard,
    'StatementSplitTask': StatementSplitTask,
    'StatementsSummaryTask': StatementsSummaryTask,
    'Step': Step,
    'StepTask': StepTask,
    'Succession': Succession,
    'SysMLBehavior': SysMLBehavior,
    'SysMLClassifier': SysMLClassifier,
    'SysMLConstraint': SysMLConstraint,
    'SysMLElement': SysMLElement,
    'SysMLExpression': SysMLExpression,
    'SysMLFeature': SysMLFeature,
    'SysMLNamespace': SysMLNamespace,
    'SysMLStructure': SysMLStructure,
    'SysMLType': SysMLType,
    'SysMLUnit': SysMLUnit,
    'SysMLUsage': SysMLUsage,
    'SystemPrompt': SystemPrompt,
    'Task': Task,
    'TaskHistory': TaskHistory,
    'TaskNode': TaskNode,
    'TaskResult': TaskResult,
    'TaskRun': TaskRun,
    'TextPart': TextPart,
    'ThinkingPart': ThinkingPart,
    'Thread': Thread,
    'ThreadTask': ThreadTask,
    'ToolCallPart': ToolCallPart,
    'ToolCallResponse': ToolCallResponse,
    'Unit': Unit,
    'UpdatesSummaryTask': UpdatesSummaryTask,
    'User': User,
    'ValidationTask': ValidationTask,
    'VersioningBranch': VersioningBranch,
    'VersioningCommit': VersioningCommit,
    'VersioningOp': VersioningOp,
    'VersioningOrigin': VersioningOrigin,
    'VersioningOverlay': VersioningOverlay,
    'VersioningPin': VersioningPin,
    'VersioningRelease': VersioningRelease,
    'VersioningRow': VersioningRow,
    'Workspace': Workspace,
  };

  return _registry;
}

/**
 * Get the model registry (initialized on first access)
 */
export const ModelRegistry = new Proxy({} as Record<string, any>, {
  get(_target, prop: string) {
    return getRegistry()[prop];
  },
  has(_target, prop: string) {
    return prop in getRegistry();
  },
  ownKeys(_target) {
    return Object.keys(getRegistry());
  }
});

/**
 * Create a model instance from API data using its label.
 * @param label - The Neo4j node label
 * @param data - The node data from API
 * @returns Instance of the appropriate model class
 */
export function createModelInstance(label: string, data: any): any {
  const ModelClass = ModelRegistry[label];
  if (!ModelClass) {
    console.warn(`No model class found for label: ${label}`);
    return data;
  }
  return new ModelClass(data);
}

// Install resolvers into @sourcepilot/framework so its base classes
// (QueryBuilderBase, NeoGraphParser, EditableRelationshipManager,
// EditableWrapper) can look up schema-aware data without statically
// importing from generated. Runs at module load — the moment any api
// file is imported, this side-effect fires and the resolvers are live.
setFrameworkResolvers({
  getRelationshipPropertyName,
  getRelationshipClass,
  modelRegistry: ModelRegistry,
  isRelationshipAllowed,
  getNestedSpec,
  getModifyConstructionSpec,
});
